class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.oldValue === this.getAttribute(mutation.attributeName)) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Reload only when a recommendation input changes
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['id', 'data-url', 'data-product-id', 'data-section-id', 'data-intent', 'data-recommendations-performed'],
    });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          this.#handleError(new Error(`La solicitud de recomendaciones devuelve HTTP ${result.status}.`));
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.classList.remove('hidden');
          delete this.dataset.error;
          this.innerHTML = recommendations.innerHTML;
        } else {
          const liquidError = html.textContent?.match(/Liquid (?:error|syntax error)[^\n]*/i)?.[0]?.slice(0, 400);
          const returnedId = html.querySelector('product-recommendations')?.id;
          const detail = liquidError || (returnedId
            ? `El identificador recibido (${returnedId}) no coincide con el esperado (${id}).`
            : `La respuesta no contiene el bloque product-recommendations (${result.data?.length || 0} caracteres).`);
          this.#handleError(new Error(detail));
        }
      })
      .catch((e) => {
        if (e.name === 'AbortError' || !this.isConnected) return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const options = window.Shopify?.designMode ? {} : { signal: controller.signal };
      const response = await fetch(url, options);
      if (controller.signal.aborted) {
        throw Object.assign(new Error('Recommendation request cancelled'), { name: 'AbortError' });
      }
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      if (controller.signal.aborted) {
        throw Object.assign(new Error('Recommendation request cancelled'), { name: 'AbortError' });
      }
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.dataset.error = error.message;
    if (window.Shopify?.designMode) {
      this.classList.remove('hidden');
      let notice = this.querySelector('[data-ohana-recommendations-diagnostic]');
      if (!notice) {
        notice = document.createElement('div');
        notice.setAttribute('data-ohana-recommendations-diagnostic', '');
        notice.setAttribute('role', 'status');
        notice.style.cssText = 'padding:20px;margin:16px;border:1px solid #b67b24;background:#fff7e8;color:#352b19;white-space:pre-wrap;overflow-wrap:anywhere;font:14px/1.5 sans-serif';
        this.prepend(notice);
      }
      notice.textContent = `Diagnóstico de la copia — visible solo en el editor\n\n${error.message}\n\nProducto: ${this.dataset.productId}\nSección: ${this.dataset.sectionId}`;
      return;
    }
    this.classList.add('hidden');
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
